import { useState } from "react";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import CloseIcon from "../../assets/svg/close-icon";
import CopyIcon from "../../assets/svg/copy-icon";
import SyncFailedIcon from "../../assets/svg/sync-failed-icon";

interface SyncFailedReasonModalProps {
  open: boolean;
  onClose: () => void;
  /** The backend's `status_failed_reason` — shown verbatim, it's the CRM's own error payload. */
  reason: string;
  /** Already formatted for display, e.g. "5 Aug, 2:32 PM". */
  syncedAt?: string;
}

const COPIED_RESET_MS = 2000;

const SyncFailedReasonModal = ({
  open,
  onClose,
  reason,
  syncedAt,
}: SyncFailedReasonModalProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(reason);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      showHeader={false}
      className="w-[600px] max-w-[90vw] rounded-lg border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-popper-bg-dk backdrop-blur-[20px] shadow-popper-blur-effect"
      bodyClassName="p-0!"
    >
      <div className="flex flex-col gap-6 px-6 py-[18px]">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <SyncFailedIcon className="shrink-0 w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
              <div className="flex flex-col gap-[2px]">
                <p className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                  Sync failed error
                </p>
                {syncedAt && (
                  <p className="text-xs text-data dark:text-data-dk">
                    {`on ${syncedAt}`}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              className="w-4 h-4 bg-gray-100 dark:bg-tag-gray-dk rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 cursor-pointer shrink-0"
              onClick={onClose}
            >
              {/* Even sizes on both: 6px in 16px leaves exactly 5px a side, so the cross can't
                  land on a half pixel the way 5.5px in 15px did. */}
              <CloseIcon className="block w-1.5 h-1.5 shrink-0 text-verba-indigo dark:text-verba-indigo-dk" />
            </button>
          </div>

          <div className="h-[84px] overflow-y-auto rounded-lg border border-border-gray dark:border-border-gray-dk bg-hover-effect dark:bg-hover-effect-dk p-3">
            <p className="text-md leading-5 break-words whitespace-pre-wrap text-verba-indigo dark:text-verba-indigo-dk">
              {reason}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            Icon={CopyIcon}
            iconLeft
            innerText={isCopied ? "Copied!" : "Copy Error"}
            onClick={handleCopy}
            buttonContainerClass="h-[26px] px-3 py-1.5 rounded-md border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
            iconClass="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk"
            innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk"
          />
          {/* The indigo background swaps to near-white in dark mode, so the label swaps with it. */}
          <Button
            innerText="Okay, got it!"
            onClick={onClose}
            buttonContainerClass="h-[26px] px-6 py-1.5 rounded-md border border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk hover:opacity-90 transition-opacity"
            innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </div>
    </Modal>
  );
};

export default SyncFailedReasonModal;
